/**
 * RecuperarContrasenaPage — «¿Has olvidado tu contraseña?».
 *
 *   /recuperar-contrasena              → pedir el enlace (correo, o documento si el
 *                                        proyecto entra con documento)
 *   /recuperar-contrasena?token=…      → crear la contraseña nueva
 *
 * Toma la marca del proyecto del dominio (logo y color). El correo con el enlace
 * lo manda el backend con la identidad de correo de ese mismo proyecto.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BACKEND_BASE } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import useRecuperarContrasena from '../hooks/useRecuperarContrasena';
import VerContrasena from '../components/VerContrasena';

const Pantalla = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.2rem;
  background: #f5f7fb;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #0b1f3a;
`;

const Tarjeta = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08);
  padding: 2rem 1.8rem;
  text-align: center;
`;

const Logo = styled.img`
  max-height: 56px;
  max-width: 220px;
  width: auto;
  margin-bottom: 1rem;
`;

const Titulo = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${p => p.$color};
`;

const Texto = styled.p`
  margin: 0 0 1.2rem;
  color: #5b6779;
  font-size: 0.93rem;
  line-height: 1.55;
`;

const Campo = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 0.9rem;
  border: 1px solid #d5dbe5;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  margin-bottom: 0.7rem;
  &:focus { outline: none; border-color: ${p => p.$color}; box-shadow: 0 0 0 3px ${p => p.$color}22; }
`;

const Boton = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1rem;
  font-size: 0.97rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: ${p => p.$bg};
  color: #fff;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.6; cursor: default; filter: none; }
`;

const Aviso = styled.div`
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 1rem;
  font-size: 0.88rem;
  line-height: 1.5;
  text-align: left;
  background: ${p => (p.$error ? '#fef2f2' : '#f0fdf4')};
  color: ${p => (p.$error ? '#991b1b' : '#166534')};
  border: 1px solid ${p => (p.$error ? '#fecaca' : '#bbf7d0')};
`;

const Volver = styled(Link)`
  display: inline-block;
  margin-top: 1.1rem;
  font-size: 0.88rem;
  color: #64748b;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const RecuperarContrasenaPage = () => {
  const { proyecto } = useProject();

  const color = proyecto?.color_primario || '#0f355b';
  const logo = proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null;
  const porDocumento = !!proyecto?.config_json?.registro_config?.login_por_dni;

  const {
    token, identificador, setIdentificador, password, setPassword, repetir, setRepetir,
    enviando, aviso, tokenOk, nombre, minimo, listo, solicitar, cambiar,
  } = useRecuperarContrasena();

  // ------------------------------------------------------ con enlace del correo
  if (token) {
    return (
      <Pantalla>
        <Tarjeta>
          {logo && <Logo src={logo} alt="" />}
          <Titulo $color={color}>Crear una contraseña nueva</Titulo>

          {tokenOk === null && <Texto>Comprobando el enlace…</Texto>}

          {aviso && <Aviso $error={aviso.error}>{aviso.texto}</Aviso>}

          {tokenOk && !listo && (
            <>
              <Texto>{nombre ? `Hola ${nombre}, escribe` : 'Escribe'} tu contraseña nueva. Mínimo {minimo} caracteres.</Texto>
              <form onSubmit={cambiar}>
                <VerContrasena flotante>
                  {tipo => (
                    <Campo $color={color} type={tipo} autoComplete="new-password" placeholder="Contraseña nueva"
                           value={password} onChange={e => setPassword(e.target.value)} autoFocus />
                  )}
                </VerContrasena>
                <VerContrasena flotante>
                  {tipo => (
                    <Campo $color={color} type={tipo} autoComplete="new-password" placeholder="Repite la contraseña"
                           value={repetir} onChange={e => setRepetir(e.target.value)} />
                  )}
                </VerContrasena>
                <Boton $bg={color} type="submit" disabled={enviando}>
                  {enviando ? 'Guardando…' : 'Guardar contraseña'}
                </Boton>
              </form>
            </>
          )}

          {listo && (
            <Boton as={Link} to="/login" $bg={color} style={{ display: 'block', textDecoration: 'none', boxSizing: 'border-box' }}>
              Ir a iniciar sesión
            </Boton>
          )}

          {tokenOk === false && <Volver to="/recuperar-contrasena">Pedir un enlace nuevo</Volver>}
          {!listo && tokenOk !== false && <Volver to="/login">← Volver a iniciar sesión</Volver>}
        </Tarjeta>
      </Pantalla>
    );
  }

  // ------------------------------------------------------ pedir el enlace
  return (
    <Pantalla>
      <Tarjeta>
        {logo && <Logo src={logo} alt="" />}
        <Titulo $color={color}>¿Has olvidado tu contraseña?</Titulo>

        {aviso && <Aviso $error={aviso.error}>{aviso.texto}</Aviso>}

        {!listo && (
          <>
            <Texto>
              {porDocumento
                ? 'Escribe tu correo o tu número de documento y te enviaremos un enlace para crear una contraseña nueva.'
                : 'Escribe tu correo y te enviaremos un enlace para crear una contraseña nueva.'}
            </Texto>
            <form onSubmit={solicitar}>
              <Campo
                $color={color}
                type={porDocumento ? 'text' : 'email'}
                autoComplete={porDocumento ? 'username' : 'email'}
                placeholder={porDocumento ? 'Correo o número de documento' : 'tu@correo.com'}
                value={identificador}
                onChange={e => setIdentificador(e.target.value)}
                autoFocus
              />
              <Boton $bg={color} type="submit" disabled={enviando || !identificador.trim()}>
                {enviando ? 'Enviando…' : 'Enviarme el enlace'}
              </Boton>
            </form>
          </>
        )}

        <Volver to="/login">← Volver a iniciar sesión</Volver>
      </Tarjeta>
    </Pantalla>
  );
};

export default RecuperarContrasenaPage;
